import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { ReactNode, useEffect, useState } from 'react';

import { fuelErrorMessage } from '../../../services/fuelReadingsService';

const REASON_MIN = 5;

// anular leitura ou aferição lançada errada: o registro fica no histórico
// com quem anulou e o motivo. os textos mudam para a liberação de turno.
export function CancelDialog({
  title,
  children,
  isOpen,
  onClose,
  onConfirm,
  reasonLabel = 'Motivo da anulação',
  placeholder = 'Ex.: digitou 30.412 em vez de 30.142',
  help = 'O registro continua no histórico, marcado como anulado, e pode ser lançado de novo.',
  confirmLabel = 'Anular',
  confirmColor = 'red',
  errorFallback = 'Não foi possível anular.',
}: {
  title: string;
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<unknown>;
  reasonLabel?: string;
  placeholder?: string;
  help?: string;
  confirmLabel?: string;
  confirmColor?: string;
  errorFallback?: string;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError('');
    }
  }, [isOpen]);

  const confirm = async () => {
    setSaving(true);
    setError('');
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (err) {
      setError(fuelErrorMessage(err, errorFallback));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg="gray.700" mx="4">
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {children}
          <Text fontSize="sm" mt="4" mb="1">
            {reasonLabel}
          </Text>
          <Textarea
            value={reason}
            onChange={event => setReason(event.target.value)}
            maxLength={300}
            rows={3}
            placeholder={placeholder}
            aria-label={reasonLabel}
          />
          <Text fontSize="xs" color="gray.500" mt="1">
            {help}
          </Text>
          {error && (
            <Alert status="error" color="red.900" borderRadius="md" mt="3">
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="3" onClick={onClose}>
            Voltar
          </Button>
          <Button
            colorScheme={confirmColor}
            onClick={confirm}
            isLoading={saving}
            isDisabled={reason.trim().length < REASON_MIN}
          >
            {confirmLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
